/**
 * @file MissedAppointmentsByStageListView.jsx
 * @author Fernando Ruano
 * @description Missed Appointments By Stage View Page
 */

// Dependencies
import { useTranslation } from 'react-i18next';
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router';
import moment from 'moment';

// Helpers
import {
  joinArrayObject,
  removeNullValuesFromObject,
  sortString
} from 'commons/helpers/utils';
import {
  createColumns,
  getColumnByType,
  getSearchApiKeys,
  getSorterString,
  PAGE_LIMIT
} from '../helper/utils';
import DashboardDetailList from '../dashboardDetailList';

function MissedAppointmentsByStageListView({
  dashboardStaffActions,
  clinicId
}) {
  const { t: translate } = useTranslation();
  const [rowData, setRowData] = React.useState([]);
  const { stage } = useParams();

  const { getMissedAppointmentsByStageListPlus } = dashboardStaffActions;

  const [totalItems, setTotalItems] = useState(0);
  const [dataFilter, setDataFilter] = useState(null);
  const [columns, setColumns] = useState([]);
  const [filter, setFilter] = useState(null);
  const [sorter, setSorter] = useState(null);

  const additionalColumns = [
    {
      title: translate('Última valoración'),
      dataIndex: 'ultima_valoracion',
      sorter: sortString('ultima_valoracion'),
      ellipsis: true,
      render: function getDateFormat(text) {
        return <span>{text ? moment.utc(text).format('DD-MM-YYYY') : ''}</span>;
      },
      filter: {
        type: 'select',
        searchName: 'filterLastValoracion',
        listName: 'listaUltimaValoracion'
      }
    }
  ];

  const getetData = (offset, pageLimit) => {
    const pageSize = pageLimit || PAGE_LIMIT;
    const joinFilter = joinArrayObject(filter);
    const joinSorter = joinArrayObject(sorter);
    const data = {
      params: {
        estadio: stage,
        idClinic: clinicId,
        limit: pageSize,
        offset,
        ...joinFilter,
        ...joinSorter
      }
    };
    getMissedAppointmentsByStageListPlus(data, (_, resp) => {
      if (resp?.data && resp.data?.totalRegistros != null) {
        setTotalItems(resp.data?.totalRegistros);
        setDataFilter(resp.data);
        setRowData(resp.data?.listaDetalles ?? []);
      }
    });
  };

  useEffect(() => {
    if (clinicId !== null) getetData(1);
  }, [clinicId]);

  useEffect(() => {
    const cols = createColumns(translate, dataFilter);
    const aditionalCols = [
      ...cols,
      ...getColumnByType(additionalColumns, dataFilter)
    ];
    setColumns(aditionalCols);
  }, [rowData, dataFilter]);

  const handleTableChange = (pagination, filters, sorterFilter) => {
    if (filters || sorterFilter) {
      const convertedFilters = getSearchApiKeys(
        columns,
        removeNullValuesFromObject(filters)
      );
      const convertedSorts = getSorterString(sorterFilter);
      setFilter(convertedFilters);
      setSorter(convertedSorts);
    }
  };

  return (
    <DashboardDetailList
      columns={columns}
      rowData={rowData}
      totalItems={totalItems}
      onPaginationChange={getetData}
      onChange={handleTableChange}
      filter={filter}
      sorter={sorter}
    />
  );
}

/**
 * Component definition of proptypes.
 */
MissedAppointmentsByStageListView.propTypes = {
  dashboardStaffActions: PropTypes.shape({
    getMissedAppointmentsByStageListPlus: PropTypes.func.isRequired
  }),
  clinicId: PropTypes.any
};

export default MissedAppointmentsByStageListView;
